import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { socket } from '../Containers/MainContainer';

// Utility functions
const numberWithCommas = (num) => num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// Skeleton Loading Component
const Skeleton = ({ className = "", variant = "default" }) => {
  const variants = {
    default: "bg-gray-700",
    light: "bg-gray-600", 
    dark: "bg-gray-800"
  };
  
  return (
    <div className={`animate-pulse ${variants[variant]} rounded ${className}`}></div>
  );
};

// Enhanced Statistics Component
const StatCard = ({ title, value, subtitle, trend, trendValue, color = "blue", isLoading = false, icon }) => {
  const colorMap = {
    blue: 'from-blue-900/40 to-blue-800/40 border-blue-700/50',
    green: 'from-green-900/40 to-green-800/40 border-green-700/50',
    yellow: 'from-yellow-900/40 to-yellow-800/40 border-yellow-700/50',
    red: 'from-red-900/40 to-red-800/40 border-red-700/50',
    purple: 'from-purple-900/40 to-purple-800/40 border-purple-700/50',
    orange: 'from-orange-900/40 to-orange-800/40 border-orange-700/50'
  };

  return (
    <div className={`bg-gradient-to-br ${colorMap[color]} p-4 rounded-xl border backdrop-blur-sm hover:bg-opacity-60 transition-all duration-300`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            {icon && <span className="text-xl">{icon}</span>}
            <p className="text-gray-300 text-xs font-medium uppercase tracking-wide">{title}</p>
          </div>
          {isLoading ? (
            <Skeleton className="w-20 h-7 mb-1" />
          ) : (
            <p className="text-2xl font-bold text-white mb-1">{value}</p>
          )}
          {subtitle && (
            isLoading ? (
              <Skeleton className="w-16 h-4" />
            ) : (
              <p className="text-gray-400 text-xs">{subtitle}</p>
            )
          )}
        </div>
        {trend && !isLoading && (
          <div className={`text-xs font-medium px-2 py-1 rounded-full ${trend === 'up' ? 'text-green-400 bg-green-400/10' : 'text-red-400 bg-red-400/10'}`}>
            <span className="text-sm">{trend === 'up' ? '↗' : '↘'}</span>
            <span className="ml-1">{trendValue}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const Button = ({ variant = "primary", size = "md", onClick, children, className = "", disabled = false }) => {
  const variants = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/20",
    secondary: "bg-gray-700 hover:bg-gray-600 text-white",
    success: "bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/20",
    danger: "bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/20",
    warning: "bg-yellow-600 hover:bg-yellow-700 text-white shadow-lg shadow-yellow-600/20"
  };
  
  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-6 py-3 text-base"
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${variants[variant]} ${sizes[size]} ${className} rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105`}
    >
      {children}
    </button>
  );
};

const Select = ({ value, onChange, children, className = "", disabled = false }) => (
  <select
    value={value}
    onChange={onChange}
    disabled={disabled}
    className={`px-4 py-2 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 backdrop-blur-sm ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    {children}
  </select>
);

const Input = ({ type = "text", placeholder, value, onChange, className = "", id, disabled = false }) => (
  <input
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    id={id}
    disabled={disabled}
    className={`px-4 py-2 bg-gray-700/50 border border-gray-600/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-200 backdrop-blur-sm ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  />
);

const Table = ({ headers, children, isLoading = false, skeletonRows = 5 }) => (
  <div className="overflow-x-auto">
    <table className="w-full">
      <thead className="bg-gray-900/50">
        <tr>
          {headers.map((header, index) => (
            <th key={index} className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-700/50">
        {isLoading ? (
          Array.from({ length: skeletonRows }, (_, index) => (
            <tr key={index} className="hover:bg-gray-800/30">
              {headers.map((_, cellIndex) => (
                <td key={cellIndex} className="px-4 py-4">
                  <Skeleton className="w-full h-4" />
                </td>
              ))}
            </tr>
          ))
        ) : children}
      </tbody>
    </table>
  </div>
);

const Card = ({ title, children, className = "", headerActions, isLoading = false }) => (
  <div className={`bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden ${className}`}>
    {title && (
      <div className="p-4 border-b border-gray-700/50 bg-gray-900/30">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{title}</h3>
          {headerActions}
        </div>
      </div>
    )}
    <div className="p-4">{children}</div>
  </div>
);

// Pagination Component
const PaginationPluto = ({ pageNumber, currentPage = 1, requestNewList }) => {
  const maxPages = Math.min(pageNumber, 10);
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(pageNumber, startPage + 4);

  return (
    <div className="flex justify-center items-center space-x-2 mt-4">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(1)}
        disabled={currentPage === 1}
      >
        First
      </Button>
      
      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Prev
      </Button>

      {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
        <Button
          key={page}
          variant={page === currentPage ? "primary" : "secondary"}
          size="sm"
          onClick={() => requestNewList(page)}
          className="min-w-[40px]"
        >
          {page}
        </Button>
      ))}

      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(currentPage + 1)}
        disabled={currentPage === pageNumber}
      >
        Next
      </Button>

      <Button
        variant="secondary"
        size="sm"
        onClick={() => requestNewList(pageNumber)}
        disabled={currentPage === pageNumber}
      >
        Last
      </Button>
    </div>
  );
};

const OffersAnalyticsDashboard = () => {
  // State management
  const [loading, setLoading] = useState(false);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [selectedWall, setSelectedWall] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [itemsPerPage] = useState(20);
  
  // Data states
  const [offersData, setOffersData] = useState([]);
  const [chargebacksData, setChargebacksData] = useState([]);
  const [showChargebacks, setShowChargebacks] = useState(false);
  const [stats, setStats] = useState({
    totalOffers: 0,
    totalChargebacks: 0,
    totalCountries: 0,
    totalRevenue: 0,
    chargebackAmount: 0,
    avgOffersPerCountry: 0
  });
  
  // Available options from backend
  const [availableWalls, setAvailableWalls] = useState([
    { value: 'all', label: 'All Walls' }
  ]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);

  // Socket event handlers and data fetching
  useEffect(() => {
    const handlers = {
      'response_get_analytics_filters': (data) => {
        console.log('Received filters:', data);
        
        // Handle walls data - Use exact wall names as values
        if (data.walls && data.walls.length > 0) {
          console.log('Raw walls from backend:', data.walls);
          
          const wallOptions = [
            { value: 'all', label: 'All Walls' },
            ...data.walls.map(wall => ({
              value: wall.wall_name, // Use exact wall name as value
              label: wall.wall_name  // Use exact wall name as label
            }))
          ];
          
          console.log('Wall options:', wallOptions);
          setAvailableWalls(wallOptions);
        }
        
        // Handle months data
        if (data.months && data.months.length > 0) {
          const monthOptions = data.months.map(month => ({
            value: month.month.toString(),
            label: new Date(2024, month.month - 1).toLocaleString('default', { month: 'long' })
          }));
          setAvailableMonths(monthOptions);
        }
        
        // Handle years data
        if (data.years && data.years.length > 0) {
          const yearOptions = data.years.map(year => ({
            value: year.year.toString(),
            label: year.year.toString()
          }));
          setAvailableYears(yearOptions);
          
          // Set current year as default if available
          const currentYear = new Date().getFullYear().toString();
          if (yearOptions.find(y => y.value === currentYear)) {
            setSelectedYear(currentYear);
          } else if (yearOptions.length > 0) {
            setSelectedYear(yearOptions[0].value);
          }
        }
        
        // Set current month as default if available
        const currentMonth = (new Date().getMonth() + 1).toString();
        if (data.months && data.months.find(m => m.month.toString() === currentMonth)) {
          setSelectedMonth(currentMonth);
        } else if (data.months && data.months.length > 0) {
          setSelectedMonth(data.months[data.months.length - 1].month.toString());
        }
        
        setFiltersLoading(false);
      },
      
      'response_offers_analytics': (data) => {
        console.log('Received analytics data:', data);
        
        if (data.offers) {
          setOffersData(data.offers);
          
          // Calculate pagination
          if (data.totalCount) {
            setTotalPages(Math.ceil(data.totalCount / itemsPerPage));
          }
        }
        setLoading(false);
      },
      
      'response_offers_analytics_stats': (data) => {
        console.log('Received stats:', data);
        
        if (data.stats && data.stats.length > 0) {
          const statsData = data.stats[0];
          setStats({
            totalOffers: parseInt(statsData.total_offers) || 0,
            totalChargebacks: parseInt(statsData.total_chargebacks) || 0,
            totalCountries: parseInt(statsData.total_countries) || 0,
            totalRevenue: parseFloat(statsData.total_revenue) || 0,
            chargebackAmount: parseFloat(statsData.chargeback_amount) || 0,
            avgOffersPerCountry: statsData.total_countries > 0 ? 
              (parseInt(statsData.total_offers) / parseInt(statsData.total_countries)).toFixed(1) : '0'
          });
        }
      },
      
      'response_chargebacks_analytics': (data) => {
        console.log('Received chargebacks data:', data);
        
        if (data.chargebacks) {
          setChargebacksData(data.chargebacks);
          
          // Update pagination for chargebacks view
          if (data.totalCount) {
            setTotalPages(Math.ceil(data.totalCount / itemsPerPage));
          }
        }
        setLoading(false);
      }
    };

    // Register event handlers
    Object.entries(handlers).forEach(([event, handler]) => {
      socket.on(event, handler);
    });

    // Fetch initial filter data
    socket.emit('getAnalyticsFilters');

    return () => {
      // Cleanup event handlers
      Object.keys(handlers).forEach(event => {
        socket.off(event);
      });
    };
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    if (selectedMonth && selectedYear && !filtersLoading) {
      setCurrentPage(1); // Reset to first page when filters change
      fetchOffersData(1);
      fetchStats();
    }
  }, [selectedWall, selectedMonth, selectedYear, searchTerm, filtersLoading]);

  const fetchOffersData = async (page = currentPage) => {
    setLoading(true);
    
    console.log('Fetching data with filters:', {
      wall: selectedWall,
      month: selectedMonth,
      year: selectedYear,
      searchTerm: searchTerm,
      page: page - 1
    });
    
    // Emit socket event to get analytics data with pagination
    socket.emit('getOffersAnalytics', {
      wall: selectedWall,
      month: selectedMonth,
      year: selectedYear,
      searchTerm: searchTerm,
      page: page - 1, // Backend expects 0-based pagination
      limit: itemsPerPage
    });
  };

  const fetchStats = async () => {
    // Emit socket event to get stats
    socket.emit('getOffersAnalyticsStats', {
      wall: selectedWall,
      month: selectedMonth,
      year: selectedYear,
      searchTerm: searchTerm
    });
  };

  const fetchChargebacksData = async (page = currentPage) => {
    setLoading(true);
    
    console.log('Fetching chargebacks with filters:', {
      wall: selectedWall,
      month: selectedMonth,
      year: selectedYear,
      searchTerm: searchTerm,
      page: page - 1
    });
    
    // Emit socket event to get chargebacks data with pagination
    socket.emit('getChargebacksAnalytics', {
      wall: selectedWall,
      month: selectedMonth,
      year: selectedYear,
      searchTerm: searchTerm,
      page: page - 1, // Backend expects 0-based pagination
      limit: itemsPerPage
    });
  };

  const handleToggleView = () => {
    setShowChargebacks(!showChargebacks);
    setCurrentPage(1);
    
    if (!showChargebacks) {
      // Switching to chargebacks view
      fetchChargebacksData(1);
    } else {
      // Switching to offers view
      fetchOffersData(1);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (showChargebacks) {
      fetchChargebacksData(page);
    } else {
      fetchOffersData(page);
    }
  };

  const handleJumpToPage = () => {
    const pageInput = document.getElementById('jumpToPage');
    const page = parseInt(pageInput.value);
    if (page && page > 0 && page <= totalPages) {
      handlePageChange(page);
      pageInput.value = '';
    }
  };

  // Generate chart data
  const chartData = offersData.reduce((acc, item) => {
    const existing = acc.find(x => x.country === item.country);
    if (existing) {
      existing.count += parseInt(item.count);
      existing.revenue += parseFloat(item.revenue);
    } else {
      acc.push({
        country: item.country,
        count: parseInt(item.count),
        revenue: parseFloat(item.revenue)
      });
    }
    return acc;
  }, []).sort((a, b) => b.count - a.count).slice(0, 10);

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <header className="bg-gray-800/60 backdrop-blur-md border-b border-gray-700/50 px-6 py-4 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">📊</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Offers Analytics Dashboard</h1>
              <p className="text-gray-400 text-sm">Analyze offer performance by wall and time period</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-xs text-gray-400">
              Last updated: {new Date().toLocaleTimeString()}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6 space-y-8">
        {/* Filters Section */}
        <Card title="Filters" className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Search Offers</label>
              <div className="relative">
                <Input
                  placeholder="Search by offer name..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pr-8"
                  disabled={filtersLoading}
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Offer Wall</label>
              <Select
                value={selectedWall}
                onChange={(e) => setSelectedWall(e.target.value)}
                className="w-full"
                disabled={filtersLoading}
              >
                {availableWalls.map(wall => (
                  <option key={wall.value} value={wall.value}>{wall.label}</option>
                ))}
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Month</label>
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full"
                disabled={filtersLoading}
              >
                <option value="">Select Month</option>
                {availableMonths.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </Select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Year</label>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full"
                disabled={filtersLoading}
              >
                <option value="">Select Year</option>
                {availableYears.map(year => (
                  <option key={year.value} value={year.value}>{year.label}</option>
                ))}
              </Select>
            </div>
            
            <div>
              <Button 
                variant="secondary" 
                onClick={handleClearSearch}
                disabled={!searchTerm && selectedWall === 'all'}
                className="w-full"
              >
                🔄 Clear Filters
              </Button>
            </div>
          </div>
        </Card>

        {/* Statistics Cards */}
        {(selectedMonth && selectedYear) && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Offers"
              value={numberWithCommas(stats.totalOffers)}
              subtitle={`${selectedWall === 'all' ? 'All walls' : availableWalls.find(w => w.value === selectedWall)?.label} • ${availableMonths.find(m => m.value === selectedMonth)?.label} ${selectedYear}`}
              icon="🎯"
              color="blue"
              isLoading={loading}
            />
            <StatCard
              title="Chargebacks"
              value={numberWithCommas(stats.totalChargebacks)}
              subtitle="Total chargebacks"
              icon="⚠️"
              color="red"
              isLoading={loading}
            />
            <StatCard
              title="Countries"
              value={stats.totalCountries}
              subtitle="Unique countries"
              icon="🌍"
              color="green"
              isLoading={loading}
            />
            <StatCard
              title="Revenue"
              value={`${numberWithCommas(stats.totalRevenue.toFixed(2))}`}
              subtitle="Total revenue"
              icon="💰"
              color="yellow"
              isLoading={loading}
            />
            
          </div>
        )}

        {/* Data Table */}
        {(selectedMonth && selectedYear) && (
          <Card 
            title={`${showChargebacks ? 'Chargebacks' : 'Offers'} Data - ${availableMonths.find(m => m.value === selectedMonth)?.label} ${selectedYear}`}
            headerActions={
              <div className="flex items-center space-x-2">
               
                <span className="text-sm text-gray-400">
                  {searchTerm && `Search: "${searchTerm}" • `}
                  Page {currentPage} of {totalPages} ({(showChargebacks ? chargebacksData : offersData).length} items)
                </span>
                <Button variant="secondary" size="sm" onClick={() => {
                  if (showChargebacks) {
                    fetchChargebacksData(currentPage);
                  } else {
                    fetchOffersData(currentPage);
                  }
                }}>
                  🔄 Refresh
                </Button>
              </div>
            }
          >
            <Table 
              headers={showChargebacks ? 
                ['Offer Name', 'Country', 'CB Count', 'Wall'] :
                ['Offer Name', 'Country', 'Completed', 'Chargebacks', 'Wall', 'Revenue']
              }
              isLoading={loading}
              skeletonRows={10}
            >
              {(showChargebacks ? chargebacksData : offersData).map((item, index) => (
                <tr key={index} className="hover:bg-gray-800/30 transition-colors">
                  <td className="px-4 py-4 text-sm text-white max-w-xs">
                    <div className="truncate" title={item.name}>
                      {item.name}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                    <span className="inline-flex items-center space-x-1">
                      <span>{item.country}</span>
                    </span>
                  </td>
                  {showChargebacks ? (
                    <>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-red-400 font-medium">
                        {numberWithCommas(item.count)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                        <span className="px-2 py-1 bg-gray-700/50 rounded-full text-xs">
                          {item.wall}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-red-400 font-medium">
                        ${parseFloat(item.revenue || 0).toFixed(2)}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-green-400 font-medium">
                        {numberWithCommas(item.count || 0)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          parseInt(item.chargeback_count || 0) > 0 ? 'bg-red-900/50 text-red-200' : 'bg-gray-700/50 text-gray-300'
                        }`}>
                          {numberWithCommas(item.chargeback_count || 0)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-300">
                        <span className="px-2 py-1 bg-gray-700/50 rounded-full text-xs">
                          {item.wall}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-green-400 font-medium">
                        ${parseFloat(item.revenue || 0).toFixed(2)}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex justify-between items-center">
                <PaginationPluto
                  pageNumber={totalPages}
                  currentPage={currentPage}
                  requestNewList={handlePageChange}
                />
                
                <div className="flex items-center space-x-2">
                  <Input
                    type="number"
                    placeholder="Page"
                    className="w-20"
                    id="jumpToPage"
                    min="1"
                    max={totalPages}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleJumpToPage}
                  >
                    Go
                  </Button>
                </div>
              </div>
            )}
            
            {(showChargebacks ? chargebacksData : offersData).length === 0 && !loading && (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">{showChargebacks ? '⚠️' : '🔍'}</div>
                <h3 className="text-lg font-medium text-white mb-2">
                  No {showChargebacks ? 'chargebacks' : 'offers'} found
                </h3>
                <p className="text-gray-400">
                  {searchTerm 
                    ? `No ${showChargebacks ? 'chargebacks' : 'offers'} found matching "${searchTerm}". Try adjusting your search term or filters.`
                    : `No ${showChargebacks ? 'chargebacks' : 'offers'} found for the selected filters. Try adjusting your search criteria.`
                  }
                </p>
                {(searchTerm || selectedWall !== 'all') && (
                  <div className="mt-4">
                    <Button variant="secondary" onClick={() => {
                      handleClearSearch();
                      setSelectedWall('all');
                    }}>
                      Clear All Filters
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Initial State */}
        {!selectedMonth || !selectedYear ? (
          <Card title="Get Started">
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎯</div>
              <h3 className="text-lg font-medium text-white mb-2">Select Filters to View Data</h3>
              <p className="text-gray-400 mb-6">
                Choose an offer wall, month, and year to analyze offer performance and see detailed statistics.
              </p>
              <div className="flex justify-center">
                <Button 
                  variant="primary" 
                  onClick={() => socket.emit('getAnalyticsFilters')}
                  disabled={filtersLoading}
                >
                  {filtersLoading ? '🔄 Loading...' : '📅 Load Filter Options'}
                </Button>
              </div>
            </div>
          </Card>
        ) : null}
      </main>
    </div>
  );
};

export default OffersAnalyticsDashboard;